"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Brain,
  Link2,
  ArrowRight,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  CheckCircle,
  BarChart3,
  Newspaper,
  Target,
  Zap,
  RefreshCw,
  ExternalLink,
  Sparkles,
  Activity,
  Eye,
  Shield,
} from "lucide-react";
import dynamic from "next/dynamic";
const TradingModal = dynamic(() => import("@/app/components/TradingModal"), { ssr: false });
import { authFetch } from "@/lib/authFetch";
import { formatCents } from '@/lib/format-cents';

interface MarketData {
  slug: string;
  question: string;
  description: string;
  image: string;
  outcomes: string[];
  outcomePrices: string[];
  volume: number;
  volume24hr: number;
  liquidity: number;
  endDate: string;
  active: boolean;
}

interface AnalysisResult {
  market: MarketData;
  analysis: {
    summary: string;
    prediction: {
      recommendedOutcome: string;
      confidence: number;
      priceTarget: number;
      riskLevel: "low" | "medium" | "high";
    };
    keyFactors: Array<{
      factor: string;
      impact: "positive" | "negative" | "neutral";
      description: string;
    }>;
    newsContext: {
      summary: string;
      sources: string[];
    };
    marketSentiment: {
      overall: "bullish" | "bearish" | "neutral";
      momentum: "increasing" | "decreasing" | "stable";
      volumeTrend: string;
    };
    riskFactors: string[];
  };
}

type Step = 1 | 2 | 3;

export default function PredictorPage() {
  const [step, setStep] = useState<Step>(1);
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [analysisStage, setAnalysisStage] = useState("");

  // Trading modal state
  const [tradingModalOpen, setTradingModalOpen] = useState(false);
  const [selectedOutcome, setSelectedOutcome] = useState<"YES" | "NO">("YES");

  const extractSlugFromUrl = (url: string): string | null => {
    const patterns = [
      /polymarket\.com\/event\/([^/?]+)/,
      /polymarket\.com\/market\/([^/?]+)/,
      /gamma\.polymarket\.com\/event\/([^/?]+)/,
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  const handleAnalyze = async () => {
    const slug = extractSlugFromUrl(url);
    if (!slug) {
      setError("Please enter a valid Polymarket URL");
      return;
    }

    setError(null);
    setLoading(true);
    setStep(2);
    setAnalysisProgress(0);

    const stages = [
      { progress: 15, stage: "Connecting to Polymarket..." },
      { progress: 30, stage: "Fetching market data..." },
      { progress: 45, stage: "Analyzing probabilities..." },
      { progress: 60, stage: "Processing sentiment..." },
      { progress: 75, stage: "Evaluating risk factors..." },
      { progress: 90, stage: "Generating prediction..." },
    ];

    let stageIndex = 0;
    const progressInterval = setInterval(() => {
      if (stageIndex < stages.length) {
        setAnalysisProgress(stages[stageIndex].progress);
        setAnalysisStage(stages[stageIndex].stage);
        stageIndex++;
      }
    }, 800);

    try {
      const response = await authFetch("/api/predictor/analyze", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url }),
      });

      clearInterval(progressInterval);

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Analysis failed");
      }

      const result = await response.json();
      setAnalysisProgress(100);
      setAnalysisStage("Complete!");

      setTimeout(() => {
        setAnalysisResult(result);
        setStep(3);
        setLoading(false);
      }, 500);
    } catch (err: unknown) {
      clearInterval(progressInterval);
      const errorMessage = err instanceof Error ? err.message : "Failed to analyze market";
      setError(errorMessage);
      setStep(1);
      setLoading(false);
    }
  };

  const resetAnalysis = () => {
    setStep(1);
    setUrl("");
    setAnalysisResult(null);
    setError(null);
  };

  const openTradingModal = (outcome: "YES" | "NO") => {
    setSelectedOutcome(outcome);
    setTradingModalOpen(true);
  };

  const getSentimentColor = (sentiment: string) => {
    switch (sentiment) {
      case "bullish":
      case "positive":
        return "text-[#00FF9D]";
      case "bearish":
      case "negative":
        return "text-[#FF1744]";
      default:
        return "text-gray-400";
    }
  };

  const getSentimentBg = (sentiment: string) => {
    switch (sentiment) {
      case "bullish":
      case "positive":
        return "bg-[#00FF9D]/10 border-[#00FF9D]/30";
      case "bearish":
      case "negative":
        return "bg-[#FF1744]/10 border-[#FF1744]/30";
      default:
        return "bg-gray-500/10 border-gray-500/30";
    }
  };

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case "low":
        return "text-[#00FF9D] bg-[#00FF9D]/10 border-[#00FF9D]/30";
      case "medium":
        return "text-[#CCFF00] bg-[#CCFF00]/10 border-[#CCFF00]/30";
      case "high":
        return "text-[#FF1744] bg-[#FF1744]/10 border-[#FF1744]/30";
      default:
        return "text-gray-400 bg-gray-400/10 border-gray-400/30";
    }
  };

  return (
    <div className="min-h-screen pb-24 lg:pb-8 relative overflow-hidden">
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-accent-primary/10 rounded-[5px] blur-[120px] animate-pulse" />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-accent-secondary/10 rounded-[5px] blur-[120px] animate-pulse" style={{ animationDelay: "1s" }} />
        <div className="hidden dark:block absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `linear-gradient(#CCFF00 1px, transparent 1px), linear-gradient(90deg, #CCFF00 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }} />
      </div>

      {/* Trading Modal */}
      {analysisResult && (
        <TradingModal
          isOpen={tradingModalOpen}
          onClose={() => setTradingModalOpen(false)}
          marketSlug={analysisResult.market.slug}
          outcome={selectedOutcome}
          outcomes={analysisResult.market.outcomes}
          outcomePrice={Number(analysisResult.market.outcomePrices?.[selectedOutcome === "YES" ? 0 : 1] || 0.5)}
          marketQuestion={analysisResult.market.question}
          marketImage={analysisResult.market.image}
        />
      )}

      {/* Content Area */}
      <div className="relative z-10 h-full">
        <AnimatePresence mode="wait">
          {/* Step 1: URL Input - Full Width */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="h-[calc(100dvh-8rem)] flex flex-col items-center justify-center relative"
            >
              {/* Floating Particles Background */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                {[...Array(20)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute w-1 h-1 bg-accent-primary/30 rounded-[5px]"
                    style={{
                      left: `${Math.random() * 100}%`,
                      top: `${Math.random() * 100}%`,
                    }}
                    animate={{
                      y: [0, -30, 0],
                      opacity: [0.2, 0.6, 0.2],
                      scale: [1, 1.5, 1],
                    }}
                    transition={{
                      duration: 3 + Math.random() * 2,
                      repeat: Infinity,
                      delay: Math.random() * 2,
                    }}
                  />
                ))}
              </div>

              {/* Main Content */}
              <div className="relative z-10 w-full max-w-4xl mx-auto">
                {/* Hero Header */}
                <motion.div
                  initial={{ opacity: 0, y: -30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="text-center mb-12"
                >
                  {/* Animated Brain Icon */}
                  <div className="relative inline-block mb-6">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                      className="absolute -inset-8 border border-dashed border-accent-primary/20 rounded-[5px]"
                    />
                    <motion.div
                      animate={{ rotate: -360 }}
                      transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                      className="absolute -inset-4 border border-accent-primary/10 rounded-[5px]"
                    />
                    <div className="absolute inset-0 bg-gradient-accent rounded-[5px] blur-2xl opacity-40 animate-pulse" />
                    <motion.div
                      animate={{ scale: [1, 1.05, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="relative w-24 h-24 rounded-[5px] bg-gradient-accent flex items-center justify-center shadow-accent"
                    >
                      <Brain className="w-12 h-12 text-white dark:text-[#0A0A0A]" />
                    </motion.div>

                    {/* Orbiting dots */}
                    {[0, 1, 2].map((i) => (
                      <motion.div
                        key={i}
                        animate={{ rotate: 360 }}
                        transition={{ duration: 4 + i, repeat: Infinity, ease: "linear" }}
                        className="absolute inset-0"
                      >
                        <div
                          className="absolute w-2 h-2 bg-accent-primary rounded-[5px] shadow-accent"
                          style={{
                            top: '-20px',
                            left: 'calc(50% - 4px)',
                          }}
                        />
                      </motion.div>
                    ))}
                  </div>

                  <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="text-5xl md:text-6xl font-monument font-bold text-gray-900 dark:text-white mb-4 tracking-tight"
                  >
                    PREDICTOR
                    <span className="ml-3 px-3 py-1 text-sm font-inter-tight bg-accent-primary/20 text-accent-primary rounded-[5px] border border-accent-primary/30 uppercase tracking-wider align-middle">
                      AI
                    </span>
                  </motion.h1>

                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="text-xl text-gray-600 dark:text-[#A1A1AA] font-satoshi max-w-lg mx-auto"
                  >
                    Advanced market intelligence powered by artificial intelligence
                  </motion.p>
                </motion.div>

                {/* Input Card */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="relative max-w-2xl mx-auto"
                >
                  {/* Card Glow */}
                  <div className="absolute -inset-1 bg-gradient-accent rounded-[5px] blur-xl opacity-30 dark:opacity-50" />

                  <div className="relative bg-white dark:bg-[#18181D]/90 backdrop-blur-2xl rounded-[5px] border border-accent-primary/20 p-8 shadow-2xl">
                    {/* Corner Accents */}
                    <div className="absolute top-0 left-0 w-16 h-16 border-l-2 border-t-2 border-accent-primary/40 rounded-tl-[5px]" />
                    <div className="absolute top-0 right-0 w-16 h-16 border-r-2 border-t-2 border-accent-primary/40 rounded-tr-[5px]" />
                    <div className="absolute bottom-0 left-0 w-16 h-16 border-l-2 border-b-2 border-accent-primary/40 rounded-bl-[5px]" />
                    <div className="absolute bottom-0 right-0 w-16 h-16 border-r-2 border-b-2 border-accent-primary/40 rounded-br-[5px]" />

                    <div className="space-y-6">
                      {/* Input Field */}
                      <div className="relative group">
                        <motion.div
                          animate={{ opacity: [0.3, 0.6, 0.3] }}
                          transition={{ duration: 2, repeat: Infinity }}
                          className="absolute -inset-0.5 bg-gradient-accent rounded-[5px] blur opacity-0 group-focus-within:opacity-100 transition-opacity duration-300"
                        />
                        <div className="relative flex items-center">
                          <div className="absolute left-4 flex items-center justify-center w-12 h-12 rounded-[5px] bg-accent-primary/10 border border-accent-primary/30">
                            <Link2 className="w-6 h-6 text-accent-primary" />
                          </div>
                          <input
                            type="text"
                            value={url}
                            onChange={(e) => {
                              setUrl(e.target.value);
                              setError(null);
                            }}
                            placeholder="Paste any Polymarket URL..."
                            className="w-full h-16 pl-20 pr-6 bg-gray-100 dark:bg-[#0F0F12]/80 border-2 border-gray-200 dark:border-[#25252B] rounded-[5px] text-lg text-gray-900 dark:text-white font-satoshi placeholder:text-gray-400 dark:placeholder:text-[#A1A1AA]/40 focus:outline-none focus:border-accent-primary/50 focus:shadow-accent transition-all duration-300"
                          />
                        </div>
                      </div>

                      {error && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="flex items-center gap-3 px-5 py-4 bg-[#FF1744]/10 border border-[#FF1744]/30 rounded-[5px]"
                        >
                          <AlertCircle className="w-5 h-5 text-[#FF1744]" />
                          <span className="text-[#FF1744] font-satoshi">{error}</span>
                        </motion.div>
                      )}

                      {/* Analyze Button */}
                      <motion.button
                        onClick={handleAnalyze}
                        disabled={!url.trim() || loading}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="relative w-full h-16 rounded-[5px] font-inter-tight font-black text-lg uppercase tracking-wide flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden group bg-gradient-accent shadow-accent hover:shadow-accent-strong"
                      >
                        {/* Shine Effect */}
                        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                          <motion.div
                            animate={{ x: ['-100%', '200%'] }}
                            transition={{ duration: 1, repeat: Infinity, repeatDelay: 0.5 }}
                            className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12"
                          />
                        </div>

                        {/* Button Content */}
                        <div className="relative flex items-center gap-3 text-white dark:text-[#0A0A0A]">
                          <Sparkles className="w-6 h-6" />
                          <span>Analyze Market</span>
                          <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform duration-300" />
                        </div>
                      </motion.button>
                    </div>
                  </div>
                </motion.div>

                {/* Quick Examples */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="flex flex-wrap gap-3 justify-center mt-8"
                >
                  <span className="text-xs text-gray-400 dark:text-[#A1A1AA]/50 font-satoshi uppercase tracking-wider self-center mr-2">Try:</span>
                  {[
                    { url: "https://polymarket.com/event/presidential-election-winner-2024", label: "US Election 2024" },
                    { url: "https://polymarket.com/event/bitcoin-above-100k", label: "Bitcoin $100K" },
                    { url: "https://polymarket.com/event/fed-rate-decision", label: "Fed Rate Decision" },
                  ].map((example, i) => (
                    <motion.button
                      key={example.label}
                      onClick={() => setUrl(example.url)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.6 + i * 0.1 }}
                      className="px-5 py-2.5 text-sm font-satoshi text-gray-600 dark:text-[#A1A1AA] hover:text-accent-primary bg-gray-100 dark:bg-[#18181D]/50 hover:bg-gray-200 dark:hover:bg-[#18181D] border border-gray-200 dark:border-[#25252B] hover:border-accent-primary/50 rounded-[5px] transition-all duration-300 flex items-center gap-2"
                    >
                      <Zap className="w-3 h-3" />
                      {example.label}
                    </motion.button>
                  ))}
                </motion.div>

                {/* Features Pills */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.7 }}
                  className="flex flex-wrap gap-4 justify-center mt-12"
                >
                  {[
                    { icon: Target, label: "AI Predictions" },
                    { icon: Activity, label: "Sentiment Analysis" },
                    { icon: Shield, label: "Risk Assessment" },
                    { icon: BarChart3, label: "Market Data" },
                  ].map((feature, i) => (
                    <motion.div
                      key={feature.label}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.8 + i * 0.1 }}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-[#25252B]/30 border border-gray-200 dark:border-[#25252B]/50 rounded-[5px]"
                    >
                      <feature.icon className="w-4 h-4 text-accent-primary" />
                      <span className="text-xs text-gray-600 dark:text-[#A1A1AA] font-satoshi">{feature.label}</span>
                    </motion.div>
                  ))}
                </motion.div>
              </div>
            </motion.div>
          )}

          {/* Step 2: Analysis in Progress - Full Width */}
          {step === 2 && loading && (
            <motion.div
              key="step2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="h-[calc(100dvh-8rem)] flex flex-col items-center justify-center"
            >
              {/* Main Loader Container */}
              <div className="relative">
                {/* Massive Outer Glow */}
                <div className="absolute -inset-32 bg-gradient-to-r from-[#CCFF00]/20 via-[#00FF9D]/10 to-[#CCFF00]/20 rounded-[5px] blur-[100px] animate-pulse" />

                {/* Scanning Lines Effect */}
                <motion.div
                  animate={{ y: [-100, 100] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  className="absolute -inset-20 overflow-hidden"
                >
                  <div className="w-full h-1 bg-gradient-to-r from-transparent via-[#CCFF00]/50 to-transparent blur-sm" />
                </motion.div>

                {/* Hexagon Grid Background */}
                <div className="absolute -inset-40 opacity-10">
                  {[...Array(6)].map((_, i) => (
                    <motion.div
                      key={i}
                      animate={{ rotate: 360 }}
                      transition={{ duration: 20 + i * 5, repeat: Infinity, ease: "linear" }}
                      className="absolute inset-0 border border-[#CCFF00]/20 rounded-[5px]"
                      style={{
                        width: `${150 + i * 60}px`,
                        height: `${150 + i * 60}px`,
                        left: `calc(50% - ${75 + i * 30}px)`,
                        top: `calc(50% - ${75 + i * 30}px)`,
                      }}
                    />
                  ))}
                </div>

                {/* Main Spinner */}
                <div className="relative w-48 h-48">
                  {/* Outer Ring - Dashed */}
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                    className="absolute inset-0 rounded-[5px] border-2 border-dashed border-[#CCFF00]/30"
                  />

                  {/* Second Ring */}
                  <motion.div
                    animate={{ rotate: -360 }}
                    transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
                    className="absolute inset-4 rounded-[5px] border-2 border-[#CCFF00]/40"
                    style={{ borderTopColor: 'transparent', borderLeftColor: 'transparent' }}
                  />

                  {/* Third Ring - Glowing */}
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                    className="absolute inset-8 rounded-[5px] border-3 border-[#CCFF00]"
                    style={{
                      borderTopColor: 'transparent',
                      borderRightColor: 'transparent',
                      boxShadow: '0 0 20px rgba(204, 255, 0, 0.5)'
                    }}
                  />

                  {/* Fourth Ring */}
                  <motion.div
                    animate={{ rotate: -360 }}
                    transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                    className="absolute inset-12 rounded-[5px] border-2 border-[#00FF9D]"
                    style={{
                      borderBottomColor: 'transparent',
                      borderLeftColor: 'transparent',
                      boxShadow: '0 0 15px rgba(0, 255, 157, 0.5)'
                    }}
                  />

                  {/* Inner Pulsing Core */}
                  <motion.div
                    animate={{ scale: [1, 1.1, 1], opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="absolute inset-16 rounded-[5px] bg-gradient-to-br from-[#CCFF00]/30 to-[#00FF9D]/30 backdrop-blur-sm"
                  />

                  {/* Center Icon */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <motion.div
                      animate={{ scale: [1, 1.15, 1], rotate: [0, 5, -5, 0] }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="relative"
                    >
                      <div className="absolute inset-0 bg-[#CCFF00] blur-xl opacity-50" />
                      <Brain className="relative w-12 h-12 text-[#CCFF00] drop-shadow-[0_0_15px_rgba(204,255,0,0.8)]" />
                    </motion.div>
                  </div>

                  {/* Orbiting Particles */}
                  {[0, 1, 2, 3].map((i) => (
                    <motion.div
                      key={i}
                      animate={{ rotate: 360 }}
                      transition={{ duration: 3 + i * 0.5, repeat: Infinity, ease: "linear" }}
                      className="absolute inset-0"
                    >
                      <motion.div
                        animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
                        transition={{ duration: 1, repeat: Infinity, delay: i * 0.25 }}
                        className="absolute w-3 h-3 rounded-[5px] bg-[#CCFF00] shadow-[0_0_15px_rgba(204,255,0,1)]"
                        style={{
                          top: '0',
                          left: 'calc(50% - 6px)',
                          transform: `rotate(${i * 90}deg) translateY(-90px)`
                        }}
                      />
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Text & Progress Section */}
              <div className="relative z-10 mt-12 text-center">
                <motion.h2
                  animate={{ opacity: [0.7, 1, 0.7] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="text-3xl font-monument font-bold text-gray-900 dark:text-white mb-3 tracking-wider"
                >
                  ANALYZING MARKET
                </motion.h2>

                <motion.p
                  key={analysisStage}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-lg text-accent-primary font-satoshi mb-8"
                >
                  {analysisStage}
                </motion.p>

                {/* Wide Progress Bar */}
                <div className="w-[400px] max-w-[90vw] mx-auto">
                  <div className="relative h-3 bg-gray-200 dark:bg-[#25252B]/50 rounded-[5px] overflow-hidden border border-gray-300 dark:border-[#25252B]">
                    <motion.div
                      className="absolute inset-y-0 left-0 bg-gradient-accent rounded-[5px] shadow-accent"
                      initial={{ width: 0 }}
                      animate={{ width: `${analysisProgress}%` }}
                      transition={{ duration: 0.5 }}
                    />
                    {/* Shimmer Effect */}
                    <motion.div
                      animate={{ x: ['-100%', '200%'] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                      className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                    />
                  </div>

                  <div className="flex justify-between items-center mt-3">
                    <span className="text-xs text-gray-500 dark:text-[#A1A1AA] font-satoshi uppercase tracking-wider">Processing AI Analysis</span>
                    <span className="text-lg text-accent-primary font-monument font-bold">
                      {analysisProgress}%
                    </span>
                  </div>
                </div>

                {/* Stage Indicators */}
                <div className="flex justify-center gap-2 mt-8">
                  {[
                    { label: "CONNECT", done: analysisProgress >= 15 },
                    { label: "FETCH", done: analysisProgress >= 30 },
                    { label: "ANALYZE", done: analysisProgress >= 45 },
                    { label: "SENTIMENT", done: analysisProgress >= 60 },
                    { label: "RISK", done: analysisProgress >= 75 },
                    { label: "PREDICT", done: analysisProgress >= 90 },
                  ].map((stage, i) => (
                    <motion.div
                      key={stage.label}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: i * 0.1 }}
                      className={`px-3 py-1.5 rounded-[5px] text-[10px] font-inter-tight font-bold uppercase tracking-wider border transition-all duration-300 ${
                        stage.done
                          ? 'bg-accent-primary/20 border-accent-primary/50 text-accent-primary shadow-accent'
                          : 'bg-gray-100 dark:bg-[#25252B]/50 border-gray-200 dark:border-[#25252B] text-gray-500 dark:text-[#A1A1AA]'
                      }`}
                    >
                      {stage.done && <CheckCircle className="w-3 h-3 inline mr-1" />}
                      {stage.label}
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 3: Results - Full Width Grid */}
          {step === 3 && analysisResult && (
            <motion.div
              key="step3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="h-[calc(100dvh-8rem)]"
            >
              {/* Top Bar with Market Info */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  {analysisResult.market.image && (
                    <div className="relative">
                      <div className="absolute -inset-0.5 bg-gradient-accent rounded-[5px] blur opacity-30" />
                      <img
                        src={analysisResult.market.image}
                        alt=""
                        className="relative w-12 h-12 rounded-[5px] object-cover"
                      />
                    </div>
                  )}
                  <div>
                    <h1 className="text-lg font-monument font-bold text-gray-900 dark:text-white line-clamp-1 max-w-xl">
                      {analysisResult.market.question}
                    </h1>
                    <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-[#A1A1AA] font-satoshi">
                      <span>Vol: ${(analysisResult.market.volume / 1000000).toFixed(1)}M</span>
                      <span>Liq: ${(analysisResult.market.liquidity / 1000).toFixed(0)}K</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={`https://polymarket.com/event/${analysisResult.market.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 rounded-[5px] bg-gray-100 dark:bg-[#18181D] border border-gray-200 dark:border-[#25252B] hover:border-accent-primary/30 text-gray-500 dark:text-[#A1A1AA] hover:text-accent-primary transition-all"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                  <button
                    onClick={resetAnalysis}
                    className="flex items-center gap-2 px-4 py-2 rounded-[5px] bg-gray-100 dark:bg-[#18181D] border border-gray-200 dark:border-[#25252B] hover:border-accent-primary/30 text-gray-600 dark:text-[#A1A1AA] hover:text-gray-900 dark:hover:text-white font-satoshi text-sm transition-all"
                  >
                    <RefreshCw className="w-4 h-4" />
                    New
                  </button>
                </div>
              </div>

              {/* Main Grid - Full Width, No Scroll */}
              <div className="grid grid-cols-12 gap-4 h-[calc(100%-4rem)]">
                {/* Left Column - Prediction & Trade */}
                <div className="col-span-12 lg:col-span-4 flex flex-col gap-4">
                  {/* AI Prediction Card */}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 }}
                    className="relative flex-1 overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-accent-primary/20 via-gray-100 dark:via-[#18181D] to-accent-secondary/10 rounded-[5px]" />
                    <div className="absolute top-0 right-0 w-32 h-32 bg-accent-primary/20 rounded-[5px] blur-[60px]" />

                    <div className="relative h-full rounded-[5px] border-2 border-accent-primary/30 p-5 flex flex-col">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-8 h-8 rounded-[5px] bg-gradient-accent flex items-center justify-center shadow-accent">
                          <Target className="w-4 h-4 text-white dark:text-[#0A0A0A]" />
                        </div>
                        <span className="text-sm font-monument font-bold text-gray-900 dark:text-white">AI PREDICTION</span>
                      </div>

                      {/* Prediction Stats */}
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <div className="bg-gray-100/50 dark:bg-white/5 backdrop-blur rounded-[5px] p-3 border border-gray-200/50 dark:border-white/10">
                          <div className="text-[10px] text-gray-500 dark:text-[#A1A1AA] uppercase mb-1">Recommended</div>
                          <div className="text-xl font-monument font-bold text-accent-primary">
                            {analysisResult.analysis.prediction.recommendedOutcome}
                          </div>
                        </div>
                        <div className="bg-gray-100/50 dark:bg-white/5 backdrop-blur rounded-[5px] p-3 border border-gray-200/50 dark:border-white/10">
                          <div className="text-[10px] text-gray-500 dark:text-[#A1A1AA] uppercase mb-1">Confidence</div>
                          <div className="text-xl font-monument font-bold text-gray-900 dark:text-white">
                            {analysisResult.analysis.prediction.confidence}%
                          </div>
                        </div>
                        <div className="bg-gray-100/50 dark:bg-white/5 backdrop-blur rounded-[5px] p-3 border border-gray-200/50 dark:border-white/10">
                          <div className="text-[10px] text-gray-500 dark:text-[#A1A1AA] uppercase mb-1">Target</div>
                          <div className="text-xl font-monument font-bold text-[#00FF9D]">
                            {formatCents(analysisResult.analysis.prediction.priceTarget)}
                          </div>
                        </div>
                        <div className="bg-gray-100/50 dark:bg-white/5 backdrop-blur rounded-[5px] p-3 border border-gray-200/50 dark:border-white/10">
                          <div className="text-[10px] text-gray-500 dark:text-[#A1A1AA] uppercase mb-1">Risk</div>
                          <div className={`inline-flex px-2 py-0.5 rounded-[5px] text-xs font-inter-tight font-bold uppercase border ${getRiskColor(analysisResult.analysis.prediction.riskLevel)}`}>
                            {analysisResult.analysis.prediction.riskLevel}
                          </div>
                        </div>
                      </div>

                      {/* Summary */}
                      <p className="text-xs text-gray-500 dark:text-gray-400 font-satoshi leading-relaxed mb-4 flex-1">
                        {analysisResult.analysis.summary}
                      </p>

                      {/* Trade Buttons */}
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => openTradingModal("YES")}
                          className="relative h-12 rounded-[5px] font-inter-tight font-bold uppercase flex items-center justify-center gap-2 overflow-hidden group bg-[#00FF9D]/10 border border-[#00FF9D]/30 hover:border-[#00FF9D] text-[#00FF9D] transition-all"
                        >
                          <TrendingUp className="w-4 h-4" />
                          <span>YES</span>
                          <span className="text-gray-500 dark:text-white/70">{formatCents(Number(analysisResult.market.outcomePrices?.[0] || 0.5))}</span>
                        </button>
                        <button
                          onClick={() => openTradingModal("NO")}
                          className="relative h-12 rounded-[5px] font-inter-tight font-bold uppercase flex items-center justify-center gap-2 overflow-hidden group bg-[#FF1744]/10 border border-[#FF1744]/30 hover:border-[#FF1744] text-[#FF1744] transition-all"
                        >
                          <TrendingDown className="w-4 h-4" />
                          <span>NO</span>
                          <span className="text-gray-500 dark:text-white/70">{formatCents(Number(analysisResult.market.outcomePrices?.[1] || 0.5))}</span>
                        </button>
                      </div>
                    </div>
                  </motion.div>
                </div>

                {/* Middle Column - Key Factors */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="col-span-12 lg:col-span-4 bg-white dark:bg-[#18181D]/80 rounded-[5px] border border-gray-200 dark:border-[#25252B] p-5 flex flex-col"
                >
                  <h3 className="text-sm font-monument font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-accent-primary" />
                    KEY FACTORS
                  </h3>
                  <div className="space-y-2 flex-1 overflow-y-auto">
                    {analysisResult.analysis.keyFactors.slice(0, 4).map((factor, i) => (
                      <div
                        key={`${factor.factor}-${i}`}
                        className={`p-3 rounded-[5px] border ${getSentimentBg(factor.impact)}`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          {factor.impact === "positive" ? (
                            <TrendingUp className="w-3 h-3 text-[#00FF9D]" />
                          ) : factor.impact === "negative" ? (
                            <TrendingDown className="w-3 h-3 text-[#FF1744]" />
                          ) : (
                            <Activity className="w-3 h-3 text-gray-400" />
                          )}
                          <span className={`text-xs font-satoshi font-semibold ${getSentimentColor(factor.impact)}`}>
                            {factor.factor}
                          </span>
                        </div>
                        <p className="text-[11px] text-gray-500 dark:text-[#A1A1AA] font-satoshi line-clamp-2">
                          {factor.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </motion.div>

                {/* Right Column - Sentiment, News, Risks */}
                <div className="col-span-12 lg:col-span-4 flex flex-col gap-4">
                  {/* Market Sentiment */}
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 }}
                    className="bg-white dark:bg-[#18181D]/80 rounded-[5px] border border-gray-200 dark:border-[#25252B] p-4"
                  >
                    <h3 className="text-sm font-monument font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                      <Activity className="w-4 h-4 text-accent-primary" />
                      SENTIMENT
                    </h3>
                    <div className="flex items-center gap-3">
                      <div className={`px-3 py-1.5 rounded-[5px] font-inter-tight font-bold uppercase text-xs border ${getSentimentBg(analysisResult.analysis.marketSentiment.overall)} ${getSentimentColor(analysisResult.analysis.marketSentiment.overall)}`}>
                        {analysisResult.analysis.marketSentiment.overall}
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] text-gray-500 dark:text-[#A1A1AA] uppercase">Momentum:</span>
                        <span className={`text-xs font-inter-tight font-bold ${
                          analysisResult.analysis.marketSentiment.momentum === "increasing" ? "text-[#00FF9D]" :
                          analysisResult.analysis.marketSentiment.momentum === "decreasing" ? "text-[#FF1744]" :
                          "text-gray-400"
                        }`}>
                          {analysisResult.analysis.marketSentiment.momentum}
                        </span>
                      </div>
                    </div>
                  </motion.div>

                  {/* News Context */}
                  {analysisResult.analysis.newsContext && (
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.4 }}
                      className="bg-white dark:bg-[#18181D]/80 rounded-[5px] border border-gray-200 dark:border-[#25252B] p-4 flex-1"
                    >
                      <h3 className="text-sm font-monument font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                        <Newspaper className="w-4 h-4 text-accent-primary" />
                        NEWS CONTEXT
                      </h3>
                      <p className="text-[11px] text-gray-500 dark:text-[#A1A1AA] font-satoshi mb-3 line-clamp-3">
                        {analysisResult.analysis.newsContext.summary}
                      </p>
                      {analysisResult.analysis.newsContext.sources?.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {analysisResult.analysis.newsContext.sources.slice(0, 3).map((source, i) => (
                            <span
                              key={`${source}-${i}`}
                              className="px-2 py-0.5 text-[10px] bg-gray-100 dark:bg-[#0F0F12] text-gray-600 dark:text-[#A1A1AA] rounded-[5px] font-satoshi border border-gray-200 dark:border-[#25252B]"
                            >
                              {source}
                            </span>
                          ))}
                        </div>
                      )}
                    </motion.div>
                  )}

                  {/* Risk Factors */}
                  {analysisResult.analysis.riskFactors.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5 }}
                      className="bg-white dark:bg-[#18181D]/80 rounded-[5px] border border-gray-200 dark:border-[#25252B] p-4"
                    >
                      <h3 className="text-sm font-monument font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                        <Shield className="w-4 h-4 text-[#FF1744]" />
                        RISKS
                      </h3>
                      <div className="space-y-1.5">
                        {analysisResult.analysis.riskFactors.slice(0, 2).map((risk, i) => (
                          <div key={`${risk.slice(0, 20)}-${i}`} className="flex items-start gap-2 p-2 bg-[#FF1744]/5 border border-[#FF1744]/20 rounded-[5px]">
                            <AlertCircle className="w-3 h-3 text-[#FF1744] mt-0.5 flex-shrink-0" />
                            <span className="text-[11px] text-gray-600 dark:text-[#A1A1AA] font-satoshi line-clamp-2">{risk}</span>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </div>
              </div>

              {/* Disclaimer */}
              <p className="text-[10px] text-gray-400 dark:text-[#A1A1AA]/50 font-satoshi text-center mt-2">
                AI analysis - not financial advice. DYOR.
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
